import type { GraphQLError } from 'graphql';
import type { ApolloClient } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import { useState } from 'react';
import type {
  CreateLostContractMutationResult,
  CreateLostContractMutationVariables,
  CreateOrderVoyageCharterRelationMutationResult,
  CreateOrderVoyageCharterRelationMutationVariables,
  CreateVoyageCharterMutationResult,
  CreateVoyageCharterMutationVariables,
  LostContractInput,
  UpdateLostContractMutationResult,
  UpdateLostContractMutationVariables,
  UpdateSpotMutationResult,
  UpdateSpotMutationVariables,
} from '../../graphql';
import {
  CreateLostContractDocument,
  CreateOrderVoyageCharterRelationDocument,
  CreateVoyageCharterDocument,
  UpdateLostContractDocument,
  UpdateSpotDocument,
} from '../../graphql';
import type { ScenarioOrder } from '../../lib/planningData';
import { assertDefined, assertedDefined, isDefined } from '../../lib/util';
import { useCurrentUser } from '../../state/zustand';

export function useSaveLostContract(): {
  isLoading: boolean;
  errors: readonly GraphQLError[] | null;
  saveLostContract: (
    orders:
      | Pick<ScenarioOrder, 'id' | 'incomingVoyageCharterRelation'>[]
      | null,
    lostContractId: number | null,
    lostContractData: LostContractInput,
  ) => Promise<{ errors?: readonly GraphQLError[] }>;
} {
  const client = useApolloClient();
  const currentUser = useCurrentUser();
  const currentOrgId = currentUser?.organizationId ?? null;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<readonly GraphQLError[] | null>(null);

  return {
    isLoading,
    errors,
    saveLostContract: async (...args) => {
      setIsLoading(true);
      setErrors(null);
      try {
        const result = await saveLostContract(
          client,
          assertedDefined(currentOrgId, 'currentOrgId'),
          ...args,
        );
        if (result.errors?.length) setErrors(result.errors ?? null);
        return result;
      } finally {
        setIsLoading(false);
      }
    },
  };
}

type SaveLostContract = ReturnType<
  typeof useSaveLostContract
>['saveLostContract'];

export async function saveLostContract(
  client: ApolloClient<unknown>,
  organizationId: number,
  ...[orders, lostContractId, lostContractData]: Parameters<SaveLostContract>
): ReturnType<SaveLostContract> {
  if (lostContractId !== null) {
    // Update lost contract
    return await client.mutate<
      UpdateLostContractMutationResult['data'],
      UpdateLostContractMutationVariables
    >({
      mutation: UpdateLostContractDocument,
      variables: {
        input: {
          id: lostContractId,
          lostContractPatch: lostContractData,
        },
      },
      update: (cache, { data }) => {
        const updated = data?.updateLostContractById?.lostContract;
        if (!updated) return;

        const updatedFields = { ...lostContractData, ...updated };
        cache.modify({
          id: cache.identify({ __typename: 'LostContract', id: updated.id }),
          fields: (v, k) => {
            const key = k.fieldName;
            return key in updatedFields
              ? updatedFields[key as keyof typeof updatedFields]
              : v;
          },
        });
      },
    });
  }

  assertDefined(
    orders,
    `Existing lost contract id or array or orders must be provided`,
  );

  // Create lost contract
  const lcResult = await client.mutate<
    CreateLostContractMutationResult['data'],
    CreateLostContractMutationVariables
  >({
    mutation: CreateLostContractDocument,
    variables: {
      input: {
        lostContract: lostContractData,
      },
    },
  });

  if (lcResult.errors?.length) return lcResult;

  const lostContractsId = lcResult.data?.createLostContract?.lostContract?.id;

  if (lostContractsId === undefined) {
    throw new Error(
      `Failed to create lost contract: ${lcResult.errors
        ?.map((e) => e.message)
        .join(', ')}`,
    );
  }

  const existingVoyageCharter =
    orders[0].incomingVoyageCharterRelation?.voyageCharterByVoyageCharterId;

  if (!isDefined(existingVoyageCharter)) {
    // Create new voyage charter
    const vcResult = await client.mutate<
      CreateVoyageCharterMutationResult['data'],
      CreateVoyageCharterMutationVariables
    >({
      mutation: CreateVoyageCharterDocument,
      variables: {
        input: {
          voyageCharter: {
            organisationId: organizationId,
            lostContractsId: lostContractsId,
            isInquiry: true,
          },
        },
      },
    });

    if (vcResult.errors?.length) return vcResult;

    const voyageCharterId =
      vcResult.data?.createVoyageCharter?.voyageCharter?.id;
    if (voyageCharterId === undefined) {
      throw new Error(
        `Failed to create voyage charter: ${vcResult.errors
          ?.map((e) => e.message)
          .join(', ')}`,
      );
    }

    const vcId = voyageCharterId;

    // Relate orders to voyage charter
    const orderRelResults = await Promise.all(
      orders.map((order) => {
        return client.mutate<
          CreateOrderVoyageCharterRelationMutationResult['data'],
          CreateOrderVoyageCharterRelationMutationVariables
        >({
          mutation: CreateOrderVoyageCharterRelationDocument,
          variables: {
            input: {
              orderVoyageCharterRelation: {
                orderId: order.id,
                voyageCharterId: vcId,
                outgoing: false,
              },
            },
          },
        });
      }),
    );
    const orderRelErrors = orderRelResults
      .map((orResult) => orResult.errors ?? [])
      .flat();
    if (orderRelErrors.length) return { errors: orderRelErrors };
  } else {
    // Update lost contract id to existing voyage charter
    const vcResult = await client.mutate<
      UpdateSpotMutationResult['data'],
      UpdateSpotMutationVariables
    >({
      mutation: UpdateSpotDocument,
      variables: {
        input: {
          id: existingVoyageCharter.id,
          voyageCharterPatch: {
            lostContractsId: lostContractsId,
          },
        },
      },
    });

    if (vcResult.errors?.length) return vcResult;
  }

  return lcResult;
}
